import React, { useState, useCallback } from 'react';
import { View, StyleSheet, FlatList, RefreshControl, Alert, TouchableOpacity } from 'react-native';
import {
  Text,
  Card,
  ActivityIndicator,
  Portal,
  Dialog,
  TextInput,
  Button,
  HelperText,
  IconButton,
} from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import productosApi from '../../services/productosApi';
import apiService from '../../services/api';

const CategoriasScreen = () => {
  const insets = useSafeAreaInsets();
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [formVisible, setFormVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [errorNombre, setErrorNombre] = useState('');
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const data = await productosApi.getCategorias();
      setCategorias(Array.isArray(data) ? data : []);
    } catch (error) {
      Alert.alert('Error', 'No se pudieron cargar las categorías');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      cargar();
    }, [cargar])
  );

  const abrirCrear = () => {
    setEditing(null);
    setNombre('');
    setDescripcion('');
    setErrorNombre('');
    setFormVisible(true);
  };

  const abrirEditar = (categoria) => {
    setEditing(categoria);
    setNombre(categoria.nombre || '');
    setDescripcion(categoria.descripcion || '');
    setErrorNombre('');
    setFormVisible(true);
  };

  const cerrarForm = () => {
    if (saving) return;
    setFormVisible(false);
  };

  const guardar = async () => {
    const nombreLimpio = nombre.trim();
    if (!nombreLimpio) {
      setErrorNombre('El nombre es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const payload = { nombre: nombreLimpio, descripcion: descripcion.trim() || null };
      if (editing) {
        await apiService.updateCategoria(editing.id, payload);
      } else {
        await apiService.createCategoria(payload);
      }
      setFormVisible(false);
      await cargar();
    } catch (error) {
      Alert.alert('Error', error.message || 'No se pudo guardar la categoría');
    } finally {
      setSaving(false);
    }
  };

  const confirmarEliminar = (categoria) => {
    Alert.alert(
      'Eliminar categoría',
      `¿Eliminar "${categoria.nombre}"? Si está en uso por productos, no se podrá borrar.`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Eliminar',
          style: 'destructive',
          onPress: async () => {
            try {
              await apiService.deleteCategoria(categoria.id);
              await cargar();
            } catch (error) {
              Alert.alert('No se pudo eliminar', error.message || 'Revisa que no esté asignada a productos.');
            }
          },
        },
      ]
    );
  };

  const renderItem = ({ item }) => (
    <Card style={styles.card} onPress={() => abrirEditar(item)}>
      <Card.Content style={styles.cardContent}>
        <View style={{ flex: 1 }}>
          <Text style={styles.nombre}>{item.nombre}</Text>
          {!!item.descripcion && <Text style={styles.descripcion}>{item.descripcion}</Text>}
        </View>
        <IconButton
          icon={({ size, color }) => <Ionicons name="trash-outline" size={size} color={color} />}
          iconColor="#DC3545"
          onPress={() => confirmarEliminar(item)}
        />
      </Card.Content>
    </Card>
  );

  if (loading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={{ marginTop: 12 }}>Cargando categorías...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={categorias}
        renderItem={renderItem}
        keyExtractor={(item) => item.id.toString()}
        contentContainerStyle={styles.list}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              cargar();
            }}
          />
        }
        ListEmptyComponent={
          <View style={styles.empty}>
            <Ionicons name="pricetags-outline" size={48} color="#adb5bd" />
            <Text style={styles.emptyText}>Aún no hay categorías</Text>
            <Text style={styles.emptyHint}>Toca el botón + para crear la primera</Text>
          </View>
        }
      />

      <TouchableOpacity style={[styles.fab, { bottom: 20 + insets.bottom }]} onPress={abrirCrear}>
        <Ionicons name="add" size={26} color="white" />
      </TouchableOpacity>

      <Portal>
        <Dialog visible={formVisible} onDismiss={cerrarForm}>
          <Dialog.Title>{editing ? 'Editar categoría' : 'Nueva categoría'}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Nombre"
              value={nombre}
              onChangeText={(t) => {
                setNombre(t);
                if (errorNombre) setErrorNombre('');
              }}
              mode="outlined"
              autoFocus
              error={!!errorNombre}
            />
            {!!errorNombre && <HelperText type="error">{errorNombre}</HelperText>}
            <TextInput
              label="Descripción (opcional)"
              value={descripcion}
              onChangeText={setDescripcion}
              mode="outlined"
              multiline
              style={{ marginTop: 12 }}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={cerrarForm} disabled={saving}>Cancelar</Button>
            <Button onPress={guardar} loading={saving} disabled={saving} mode="contained">
              {editing ? 'Guardar' : 'Crear'}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8f9fa' },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  list: { padding: 12, paddingBottom: 96 },
  card: { marginBottom: 8, backgroundColor: 'white' },
  cardContent: { flexDirection: 'row', alignItems: 'center', paddingVertical: 4 },
  nombre: { fontSize: 16, fontWeight: '600', color: '#212529' },
  descripcion: { fontSize: 13, color: '#6c757d', marginTop: 2 },
  empty: { alignItems: 'center', paddingVertical: 64 },
  emptyText: { fontSize: 16, color: '#6c757d', marginTop: 12 },
  emptyHint: { fontSize: 13, color: '#adb5bd', marginTop: 4 },
  fab: {
    position: 'absolute',
    right: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#0066cc',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 4,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
  },
});

export default CategoriasScreen;
